import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Badge,
  Card,
  CardBody,
  Link,
  Divider,
  Select,
  Button,
} from '@chakra-ui/react';
import { CalendarIcon, TimeIcon, EmailIcon } from '@chakra-ui/icons';
import { QUERY_MATCHES } from '../../../Queries';

export const DisciplineSchedule = ({ discipline, disciplineName }) => {
  const [timeRange, setTimeRange] = useState('6'); // Default to 6 months
  const [showAll, setShowAll] = useState(false);

  // Calculate date range for query
  const { startDate, endDate } = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    if (showAll) {
      // Query all future matches (up to 5 years out)
      return {
        startDate: now,
        endDate: now + (5 * 365 * 24 * 60 * 60)
      };
    }
    const monthsInSeconds = parseInt(timeRange) * 30 * 24 * 60 * 60;
    return {
      startDate: now,
      endDate: now + monthsInSeconds
    };
  }, [timeRange, showAll]);

  // Single query for all matches in date range for this discipline
  const { data, loading } = useQuery(QUERY_MATCHES, {
    variables: {
      match_discipline: discipline,
      start_date: startDate,
      end_date: endDate,
    },
  });

  // Sort matches by date
  const allMatches = useMemo(() => {
    const matches = data?.queryMatches || [];
    return [...matches].sort((a, b) => 
      a.match_details.match_date - b.match_details.match_date
    );
  }, [data]);

  const filteredMatches = allMatches;

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateShort = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isUpcoming = (timestamp) => {
    return timestamp * 1000 > Date.now();
  };

  // Generate ICS calendar file for download
  const generateICS = (match) => {
    const details = match.match_details;
    const startDate = new Date(details.match_date * 1000);
    
    // Format date for ICS (YYYYMMDDTHHMMSSZ)
    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    // Assume match starts at 9 AM and lasts 4 hours
    const startDateTime = new Date(startDate);
    startDateTime.setHours(9, 0, 0, 0);
    const endDateTime = new Date(startDateTime);
    endDateTime.setHours(13, 0, 0, 0);
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Golden Gun Club//Match Schedule//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `DTSTART:${formatICSDate(startDateTime)}`,
      `DTEND:${formatICSDate(endDateTime)}`,
      `DTSTAMP:${formatICSDate(new Date())}`,
      `UID:${match.match_id}@goldengunclub.us`,
      `SUMMARY:${details.match_name}`,
      `DESCRIPTION:${details.match_description || disciplineName + ' match at Golden Gun Club'}`,
      `LOCATION:${details.match_location || 'Golden Gun Club, 1503 S Watkins Rd, Watkins, CO 80137'}`,
      `STATUS:${details.match_status === 'CANCELLED' ? 'CANCELLED' : 'CONFIRMED'}`,
      details.match_director ? `ORGANIZER;CN=${details.match_director}:mailto:${details.match_director_email || 'competitions@goldengunclub.us'}` : '',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line).join('\r\n');
    
    return icsContent;
  };

  const downloadICS = (match) => {
    const icsContent = generateICS(match);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${match.match_details.match_name.replace(/[^a-z0-9]/gi, '_')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  if (loading) {
    return (
      <Box mt={6} textAlign="center">
        <Spinner size="lg" />
        <Text mt={4}>Loading schedule...</Text>
      </Box>
    );
  }

  if (allMatches.length === 0) {
    return (
      <Box mt={6}>
        <Heading size="md" mb={4}>
          {disciplineName} Schedule
        </Heading>
        <Text color="gray.600" mb={4}>
          No upcoming matches scheduled at this time. Check back soon for future competitions.
        </Text>
        <Text fontSize="sm">
          For more information or to schedule a match, contact{' '}
          <Link href="mailto:competitions@goldengunclub.us" color="blue.500">
            competitions@goldengunclub.us
          </Link>
        </Text>
      </Box>
    );
  }

  return (
    <Box mt={6}>
      <HStack justify="space-between" align="start" mb={4} flexWrap="wrap" gap={4}>
        <Heading size="md">
          {disciplineName} Schedule
        </Heading>
        
        {allMatches.length > 0 && (
          <HStack spacing={2} flexWrap="wrap">
            <Text fontSize="sm" color="gray.600">Show matches for:</Text>
            <Select 
              size="sm" 
              width="auto" 
              value={showAll ? 'all' : timeRange}
              onChange={(e) => {
                if (e.target.value === 'all') {
                  setShowAll(true);
                } else {
                  setShowAll(false);
                  setTimeRange(e.target.value);
                }
              }}
            >
              <option value="3">Next 3 months</option>
              <option value="6">Next 6 months</option>
              <option value="12">Next 12 months</option>
              <option value="all">All upcoming</option>
            </Select>
          </HStack>
        )}
      </HStack>

      {filteredMatches.length === 0 && allMatches.length > 0 ? (
        <Box>
          <Text color="gray.600" mb={4}>
            No matches scheduled in the selected time range.
          </Text>
          <Button 
            size="sm" 
            colorScheme="blue" 
            variant="outline"
            onClick={() => setShowAll(true)}
          >
            Show all upcoming matches
          </Button>
        </Box>
      ) : filteredMatches.length === 0 ? (
        <Box>
          <Text color="gray.600" mb={4}>
            No upcoming matches scheduled at this time. Check back soon for future competitions.
          </Text>
          <Text fontSize="sm">
            For more information or to schedule a match, contact{' '}
            <Link href="mailto:competitions@goldengunclub.us" color="blue.500">
              competitions@goldengunclub.us
            </Link>
          </Text>
        </Box>
      ) : (
        <>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Showing {filteredMatches.length} of {allMatches.length} scheduled match{allMatches.length !== 1 ? 'es' : ''}
          </Text>

          <VStack align="stretch" spacing={3}>
            {filteredMatches.map((match) => {
              const details = match.match_details;
              const isInProgress = details.match_status === 'IN_PROGRESS';
              const isCancelled = details.match_status === 'CANCELLED';
              
              return (
                <Card 
                  key={match.match_id} 
                  variant="outline"
                  bg={isCancelled ? 'red.50' : 'white'}
                  borderColor={isCancelled ? 'red.300' : 'gray.200'}
                  opacity={isCancelled ? 0.85 : 1}
                  size="sm"
                >
                  <CardBody py={3} px={4}>
                    <VStack align="stretch" spacing={3}>
                      {/* Header with title and status */}
                      <HStack justify="space-between" align="start" spacing={3}>
                        <Heading 
                          size="sm" 
                          flex={1}
                          textDecoration={isCancelled ? 'line-through' : 'none'}
                          color={isCancelled ? 'red.600' : 'gray.800'}
                        >
                          {details.match_name}
                        </Heading>
                        <Badge 
                          colorScheme={isCancelled ? 'red' : isInProgress ? 'green' : 'blue'}
                          fontSize="xs"
                          px={2}
                          py={1}
                        >
                          {isCancelled ? 'CANCELLED' : isInProgress ? 'In Progress' : 'Upcoming'}
                        </Badge>
                      </HStack>

                      {isCancelled && (
                        <Text fontSize="sm" color="red.600" fontWeight="semibold">
                          ⚠️ This match has been cancelled
                        </Text>
                      )}

                      {/* Match details in compact single column */}
                      <VStack align="stretch" spacing={1.5} fontSize="sm">
                        {/* Date - clickable to download ICS */}
                        <HStack spacing={2}>
                          <CalendarIcon color="blue.500" boxSize={3.5} flexShrink={0} />
                          <Link
                            onClick={(e) => {
                              e.preventDefault();
                              downloadICS(match);
                            }}
                            color="blue.600"
                            fontSize="sm"
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline', color: 'blue.700' }}
                            title="Add to calendar"
                          >
                            {formatDate(details.match_date)}
                          </Link>
                        </HStack>

                        {/* Location - clickable */}
                        {details.match_location && (
                          <HStack spacing={2}>
                            <Box fontSize="sm" flexShrink={0}>📍</Box>
                            {details.match_location === 'Golden Gun Club' ? (
                              <Link
                                href="/about-us"
                                color="blue.600"
                                fontSize="sm"
                                _hover={{ textDecoration: 'underline', color: 'blue.700' }}
                              >
                                {details.match_location}
                              </Link>
                            ) : (
                              <Link
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(details.match_location)}`}
                                isExternal
                                color="blue.600"
                                fontSize="sm"
                                _hover={{ textDecoration: 'underline', color: 'blue.700' }}
                              >
                                {details.match_location}
                              </Link>
                            )}
                          </HStack>
                        )}

                        {/* Match Director - clickable email */}
                        {details.match_director && (
                          <HStack spacing={2}>
                            <EmailIcon color="blue.500" boxSize={3.5} flexShrink={0} />
                            <HStack spacing={1} flexWrap="wrap">
                              <Text fontSize="sm" color="gray.700">
                                {details.match_director}
                              </Text>
                              {details.match_director_email && (
                                <Link
                                  href={`mailto:${details.match_director_email}`}
                                  color="blue.600"
                                  fontSize="sm"
                                  _hover={{ textDecoration: 'underline', color: 'blue.700' }}
                                >
                                  ({details.match_director_email})
                                </Link>
                              )}
                            </HStack>
                          </HStack>
                        )}

                        {/* Registration deadline */}
                        {details.registration_deadline && isUpcoming(details.registration_deadline) && (
                          <HStack spacing={2}>
                            <TimeIcon color="orange.500" boxSize={3.5} flexShrink={0} />
                            <Text fontSize="sm" color="gray.700">
                              Registration deadline: {formatDateShort(details.registration_deadline)}
                            </Text>
                          </HStack>
                        )}
                      </VStack>

                      {/* Description */}
                      {details.match_description && (
                        <Text fontSize="sm" color="gray.600" noOfLines={3} lineHeight="1.5">
                          {details.match_description}
                        </Text>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              );
            })}
          </VStack>

          {/* Contact info */}
          <Box mt={6} fontSize="sm" color="gray.600">
            For questions about scheduled matches, contact{' '}
            <Link href="mailto:competitions@goldengunclub.us" color="blue.500">
              competitions@goldengunclub.us
            </Link>
          </Box>
        </>
      )}
    </Box>
  );
};
